import DropdownMenu from '@jetbrains/ring-ui/components/dropdown-menu/dropdown-menu'
import type {WrapTextProps} from '@jetbrains/ring-ui/components/link/link'
import Tooltip from '@jetbrains/ring-ui/components/tooltip/tooltip'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import deepEqual from 'fast-deep-equal'
import type {ComponentType} from 'react'
import {memo, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'

import {useIsHighlightMyChangesAndInvestigations} from '../../../../../hooks/useIsHighlightMyChangesAndInvestigations'
import useMyId from '../../../../../hooks/useMyId'
import {useSlice} from '../../../../../hooks/useSlice'
import type {State} from '../../../../../reducers/types'
import {getChangesArg} from '../../../../../rest/arguments'
import type {User} from '../../../../../services/rest'
import {restApi} from '../../../../../services/rest'
import type {BuildId} from '../../../../../types'
import {emptyArray} from '../../../../../utils/empty'
import {notNull} from '../../../../../utils/guards'
import Avatar, {Size} from '../../../../common/Avatar/Avatar.container'
import RouterLink from '../../../../common/RouterLink/RouterLink'
import {getBuildChanges} from '../../Changes.selectors'
import {getSingleUser} from '../../Changes.utils'

import {getChangesByIds} from './ChangesAvatars.selectors'

import styles from './ChangesAvatars.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId?: BuildId
  locator?: string
  className?: string
  othersClassName?: string
  href: string | null | undefined
}

type UserWithVCSUserName = Partial<User> & {
  vcsUserName?: string | undefined
}

type Author = {
  user: UserWithVCSUserName
  count: number
}

const LIMIT = 3

const ChangesAvatars = memo(({buildId, locator, className, othersClassName = '', href}: Props) => {
  const isHighlightMyChanges = useIsHighlightMyChangesAndInvestigations()
  const {changeIds} = restApi.endpoints.getAllChanges.useQuery(
    buildId == null && locator != null ? getChangesArg(locator) : skipToken,
    {selectFromResult: ({data}) => ({changeIds: data?.changes?.result ?? emptyArray})},
  )
  const getChanges = useCallback(
    (state: State) => {
      if (buildId != null) {
        return getBuildChanges(state, buildId)
      }
      if (locator != null) {
        return getChangesByIds(state, changeIds)
      }
      return []
    },
    [buildId, changeIds, locator],
  )

  const users = useAppSelector(
    state =>
      getChanges(state)
        .map(change => {
          const user = getSingleUser(change)
          return user?.id != null || change.username != null
            ? Object.assign({vcsUserName: change.username}, user)
            : null
        })
        .filter(notNull),
    deepEqual,
  )
  const myId = useMyId()
  const authors: Record<string, Author> = {}
  for (const user of users) {
    const key = user.id != null ? `userId:${user.id}` : `vcsUserName:${user.vcsUserName}`
    if (authors[key] != null) {
      authors[key].count++
    } else {
      authors[key] = {user, count: 1}
    }
  }
  const existingUsers = Object.values(authors)
    .sort((a, b) => {
      if (a.user.id === myId) {
        return -1
      }
      if (b.user.id === myId) {
        return 1
      }
      if (a.user.avatars && !b.user.avatars) {
        return -1
      }
      if (!a.user.avatars && b.user.avatars) {
        return 1
      }
      return b.count - a.count
    })
    .map(author => author.user)
  const shownUsers = existingUsers.slice(0, LIMIT)
  const extraUsers = existingUsers.slice(LIMIT)

  const renderUser = (user: UserWithVCSUserName) => (
    <Avatar
      user={user}
      vcsUserName={user.vcsUserName}
      className={styles.avatarWrapper}
      avatarClassName={classNames(styles.avatar, {
        [othersClassName]: isHighlightMyChanges && user.id !== myId,
        [styles.othersAvatar]: isHighlightMyChanges && user.id !== myId,
      })}
      size={Size.Size20}
    />
  )
  type TogglePayloads = {
    onShow: void
    onHide: void
  }
  const [dropdownOpen, {onShow, onHide}] = useSlice<boolean, TogglePayloads>(
    {
      onShow: () => true,
      onHide: () => false,
    },
    false,
  )
  const navigate = useNavigate()
  const getUserHref = (user: UserWithVCSUserName) =>
    `${href}&username=${user.vcsUserName && encodeURIComponent(user.vcsUserName)}`

  return shownUsers.length > 0 ? (
    <span
      className={classNames(styles.avatars, className, {
        [styles.hovered]: dropdownOpen,
      })}
    >
      {shownUsers.map(user => (
        <Tooltip
          popupProps={{top: -1, offset: 4}}
          className={styles.item}
          key={user.id ?? user.vcsUserName}
          title={user.name ?? user.username ?? user.vcsUserName}
        >
          {href != null ? (
            <RouterLink
              className={styles.link}
              aria-label={user.name ?? user.username ?? user.vcsUserName}
              to={getUserHref(user)}
            >
              {() => renderUser(user)}
            </RouterLink>
          ) : (
            renderUser(user)
          )}
        </Tooltip>
      ))}
      {extraUsers.length > 0 && (
        <DropdownMenu
          hoverMode
          hoverShowTimeOut={10}
          onShow={onShow}
          onHide={onHide}
          menuProps={{top: -1, offset: 4, closeOnSelect: false}}
          className={styles.extra}
          anchor={
            <button type="button" className={styles.extraText}>{`+${extraUsers.length}`}</button>
          }
          data={extraUsers.map(user => {
            const userHref = getUserHref(user)
            return {
              key: user.id ?? user.vcsUserName,
              children: (WrapText: ComponentType<WrapTextProps>) => (
                <>
                  <Avatar
                    user={user}
                    vcsUserName={user.vcsUserName}
                    size={Size.Size20}
                    className={styles.listAvatarWrapper}
                    avatarClassName={styles.listAvatar}
                  />
                  <WrapText>{user.name ?? user.username ?? user.vcsUserName}</WrapText>
                </>
              ),
              href: userHref,
              onPlainLeftClick: () => navigate(userHref),
            }
          })}
        />
      )}
    </span>
  ) : null
})
ChangesAvatars.displayName = 'ChangesAvatars'

export default ChangesAvatars
